import React, { useState, useEffect } from 'react';
import axios from 'axios';
import QuestionRenderer from './components/QuestionRenderer';
import './App.css';

function App() {
  const [companyId, setCompanyId] = useState("");
  const [companies, setCompanies] = useState([]);
  const [companyEntered, setCompanyEntered] = useState(false);
  const [isNewCompany, setIsNewCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios.get("https://api.discovery.quantecsystems.com/companies")
      .then(res => setCompanies(res.data))
      .catch(err => console.error("Failed to load companies", err));

    axios.get("https://api.discovery.quantecsystems.com/questions").then(res => {
      const grouped = {};
      for (const q of res.data) {
        if (!grouped[q.category]) grouped[q.category] = [];
        grouped[q.category].push(q);
      }
      setQuestions(grouped);
      const ordered = [
        "Company Overview",
        "User & Endpoint Landscape",
        "Network & Infrastructure",
        "Cloud & Software Tools",
        "Vendors & Contracts",
        "Pain Points & Priorities"
      ];
      const filtered = ordered.filter(c => Object.keys(grouped).includes(c));
      setCategories(filtered);
    });
  }, []);

  const currentCategory = categories[currentCategoryIndex];
  const currentQuestions = questions[currentCategory] || [];
  const noteField = currentQuestions.find(q => q.meta?.isNoteField);
  const mainQuestions = currentQuestions.filter(q => !q.meta?.isNoteField);

  const handleAnswerChange = (questionId, response) => {
    setAnswers(prev => ({ ...prev, [questionId]: response }));
  };

  const handleNext = () => {
    if (currentCategoryIndex < categories.length - 1) {
      setCurrentCategoryIndex(currentCategoryIndex + 1);
    } else {
      submitAllAnswers();
    }
  };

  const handleBack = () => {
    if (currentCategoryIndex > 0) {
      setCurrentCategoryIndex(currentCategoryIndex - 1);
    }
  };

  const handleCompanyProceed = async () => {
    if (isNewCompany) {
      if (!newCompanyName.trim()) {
        alert("Please enter a company name.");
        return;
      }

      try {
        const res = await axios.post("https://api.discovery.quantecsystems.com/companies", {
          name: newCompanyName.trim()
        });

        // Add the new company to the companies list
        setCompanies(prev => [...prev, res.data]);
        setCompanyId(res.data.companyId);
        setCompanyEntered(true);
      } catch (err) {
        alert("Failed to create company.");
        console.error(err);
      }
    } else {
      if (!companyId) {
        alert("Please select a company.");
        return;
      }
      setCompanyEntered(true);
    }
  };

  const submitAllAnswers = async () => {
    try {
      const payload = {
        companyId: companyId,
        answers: Object.entries(answers).map(([questionId, answer]) => ({
          questionId,
          answer,
        })),
      };

      const res = await axios.post("https://api.discovery.quantecsystems.com/submit-assessment", payload);

      if (res.status === 200) {
        alert("All answers submitted! Session ID: " + res.data.sessionId);
      } else {
        alert("Error submitting assessment.");
      }
    } catch (err) {
      console.error("Submit error:", err);
      alert("Submission failed.");
    }
  };

  if (!companyEntered) {
    return (
      <div className="container">
        <div className="question-box">
          <h1>Select or Create a Company</h1>

          <select
            className="company-input"
            onChange={(e) => {
              const val = e.target.value;
              if (val === "new") {
                setIsNewCompany(true);
                setCompanyId("");
              } else {
                setIsNewCompany(false);
                setCompanyId(val);
              }
            }}
            value={isNewCompany ? "new" : companyId}
          >
            <option value="">-- Select Company --</option>
            {companies.map((c) => (
              <option key={c.companyId} value={c.companyId}>{c.name}</option>
            ))}
            <option value="new">+ Create New Company</option>
          </select>

          {isNewCompany && (
            <input
              type="text"
              placeholder="Enter new company name"
              className="company-input"
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e.target.value)}
              style={{ marginTop: '10px', width: '100%', padding: '10px' }}
            />
          )}

          <div className="nav-buttons" style={{ marginTop: "20px" }}>
            <button onClick={handleCompanyProceed}>Begin Discovery Call</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="question-box">
        <h1>{currentCategory}</h1>

        {mainQuestions.map(q => (
          <div key={q.questionId} className="question-block">
            <h2>{q.prompt}</h2>
            {q.meta?.guidance && (
              <div className="guidance"><strong>Guidance:</strong> {q.meta.guidance}</div>
            )}
            <QuestionRenderer question={q} onChange={handleAnswerChange} />
          </div>
        ))}

        {noteField && (
          <div className="question-block">
            <h2>{noteField.prompt}</h2>
            <QuestionRenderer question={noteField} onChange={handleAnswerChange} />
          </div>
        )}

        <div className="nav-buttons">
          <button onClick={handleBack} disabled={currentCategoryIndex === 0}>Back</button>
          <button onClick={handleNext}>
            {currentCategoryIndex < categories.length - 1 ? "Next" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;
